import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillTransfer, faUniversity, faCheckCircle, faWallet,
  faArrowLeft, faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { bankList } from '../utils/bankList';
import { UiPageHeader, UiButton, UiInput, UiSelect, FIELD_INPUT_CLASS, FIELD_LABEL_CLASS } from '../components/ui';

interface PayoutResult {
  id: string;
  payout_number: string;
  amount: number;
  fee_amount: number;
  net_amount: number;
  bank_account_name: string;
  bank_account_number: string;
  bank_name: string;
  status: string;
}

const CreatePayoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    amount: '',
    bank_name: '',
    bank_account_number: '',
    bank_account_name: '',
    note: '',
    callback_url: '',
  });
  const [payoutBalance, setPayoutBalance] = useState<number | null>(null);
  const [payoutResult, setPayoutResult] = useState<PayoutResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [payoutFeePercentage, setPayoutFeePercentage] = useState<number>(0);

  useEffect(() => {
    loadWallet();
    loadProfile();
  }, []);

  const loadWallet = async () => {
    try {
      const res = await merchantApi.get('/merchants/wallet');
      if (res.data.success) {
        setPayoutBalance(res.data.data.payout_balance ?? 0);
      }
    } catch {
      // ไม่แสดง error ถ้าโหลด wallet ไม่ได้
    }
  };

  const loadProfile = async () => {
    try {
      const res = await merchantApi.get('/merchants/me');
      if (res.data.success) {
        setPayoutFeePercentage(parseFloat(res.data.data.payout_fee_percentage) || 0);
      }
    } catch {
      // ไม่แสดง error
    }
  };

  // คำนวณ fee preview
  const amountNum = parseFloat(formData.amount) || 0;
  const feeAmount = Math.round(amountNum * (payoutFeePercentage / 100) * 100) / 100;
  const totalDeduct = amountNum + feeAmount;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error(t('payout.amountRequired'));
      return;
    }
    if (!formData.bank_name || !formData.bank_account_number || !formData.bank_account_name) {
      toast.error(t('payout.accountInfoRequired'));
      return;
    }

    try {
      setLoading(true);
      const res = await merchantApi.post('/merchants/payouts', {
        amount: parseFloat(formData.amount),
        bank_name: formData.bank_name,
        bank_account_number: formData.bank_account_number,
        bank_account_name: formData.bank_account_name,
        note: formData.note || undefined,
        callback_url: formData.callback_url || undefined,
      });

      if (res.data.success) {
        setPayoutResult(res.data.data);
        toast.success(t('payout.createSuccess'));
        setFormData({
          amount: '',
          bank_name: '',
          bank_account_number: '',
          bank_account_name: '',
          note: '',
          callback_url: '',
        });
        loadWallet();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('payout.createError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faMoneyBillTransfer}
        title={t('payout.createNew')}
        subtitle={t('payout.createDesc')}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6">
          <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 mb-6 flex items-center gap-2">
            <FontAwesomeIcon icon={faUniversity} style={{ color: 'var(--theme-primary)' }} />
            {t('payout.payoutInfo')}
          </h2>

          {/* Wallet Balance */}
          {payoutBalance !== null && (
            <div className="mb-5 p-4 rounded-xl ring-1 ring-inset ring-primary/15 flex items-center gap-3" style={{ background: 'var(--theme-primary-light)' }}>
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15 flex items-center justify-center">
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--theme-primary)' }}>{t('payout.walletBalance')}</p>
                <p className="text-xl font-bold" style={{ color: 'var(--theme-accent)', fontVariantNumeric: 'tabular-nums' }}>฿{payoutBalance.toLocaleString()}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Amount */}
            <div>
              <UiInput
                label={t('payout.amountBaht')}
                required
                prefix="฿"
                type="number"
                step="0.01"
                min="1"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                className="text-lg font-semibold"
              />

              {/* Fee Preview */}
              {amountNum > 0 && payoutFeePercentage > 0 && (
                <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 text-sm">
                  <div className="flex justify-between text-gray-600 dark:text-slate-300">
                    <span>{t('payout.withdrawAmount')}</span>
                    <span className="font-semibold">฿{amountNum.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-amber-700 dark:text-amber-400 mt-1">
                    <span>{t('payout.fee')} ({payoutFeePercentage}%)</span>
                    <span className="font-semibold">฿{feeAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <hr className="my-2 border-amber-200 dark:border-amber-800" />
                  <div className="flex justify-between text-gray-800 dark:text-slate-100 font-bold">
                    <span>{t('payout.totalDeducted')}</span>
                    <span>฿{totalDeduct.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Bank */}
            <UiSelect
              label={t('common.bank')}
              icon={faUniversity}
              required
              value={formData.bank_name}
              onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
            >
              <option value="">{t('common.selectBank')}</option>
              {bankList.filter(bank => bank.isActive).map(bank => (
                <option key={bank.bankCode} value={bank.shortName}>
                  {bank.shortName} - {bank.bankName}
                </option>
              ))}
            </UiSelect>

            {/* Account Number */}
            <UiInput
              label={t('common.bankAccount')}
              required
              type="text"
              value={formData.bank_account_number}
              onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
              placeholder={t('payout.digits')}
              pattern="[0-9]{10,15}"
            />

            {/* Account Name */}
            <UiInput
              label={t('common.accountName')}
              required
              type="text"
              value={formData.bank_account_name}
              onChange={(e) => setFormData({ ...formData, bank_account_name: e.target.value })}
              placeholder={t('payout.fullNamePlaceholder')}
            />

            {/* Note */}
            <div>
              <label className={FIELD_LABEL_CLASS}>{t('common.note')}</label>
              <textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder={t('payout.notePlaceholder')}
                rows={3}
                className={`${FIELD_INPUT_CLASS} resize-none`}
              />
            </div>

            {/* Callback URL */}
            <UiInput
              label="Callback URL"
              type="url"
              value={formData.callback_url}
              onChange={(e) => setFormData({ ...formData, callback_url: e.target.value })}
              placeholder="https://example.com/callback"
            />

            {/* Submit */}
            <UiButton type="submit" loading={loading} icon={faMoneyBillTransfer} className="w-full justify-center">
              {loading ? t('common.creating') : t('payout.submitPayout')}
            </UiButton>
          </form>
        </div>

        {/* Result / Empty State */}
        {payoutResult ? (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <FontAwesomeIcon icon={faCheckCircle} />
                {t('payout.submitSuccess')}
              </h2>
              <p className="text-white/70 text-sm mt-1">{t('payout.waitApproval')}</p>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">{t('payout.payoutNumber')}</p>
                  <p className="font-bold text-gray-800 dark:text-slate-100">{payoutResult.payout_number}</p>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">สถานะ</p>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-full text-xs font-semibold">
                    {t('payout.pendingApproval')}
                  </span>
                </div>
                <div className="rounded-xl p-4 col-span-2 ring-1 ring-inset ring-primary/15" style={{ background: 'var(--theme-primary-light)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--theme-primary)' }}>{t('payout.amount')}</p>
                  <p className="font-bold text-2xl" style={{ color: 'var(--theme-accent)' }}>฿{payoutResult.amount.toLocaleString()}</p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2">
                <p className="text-xs text-gray-500 dark:text-slate-400 font-medium">{t('payout.destinationAccount')}</p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-slate-400">{t('common.bank')}</span>
                  <span className="font-semibold text-gray-800 dark:text-slate-100">{payoutResult.bank_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-slate-400">{t('common.bankAccount')}</span>
                  <code className="font-mono font-semibold text-gray-800 dark:text-slate-100">{payoutResult.bank_account_number}</code>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-slate-400">{t('common.accountName')}</span>
                  <span className="font-semibold text-gray-800 dark:text-slate-100">{payoutResult.bank_account_name}</span>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <UiButton variant="secondary" icon={faArrowLeft} onClick={() => navigate('/payouts')} className="flex-1 justify-center">
                  {t('payout.viewPayouts')}
                </UiButton>
                <UiButton icon={faPlus} onClick={() => setPayoutResult(null)} className="flex-1 justify-center">
                  {t('payout.createAnother')}
                </UiButton>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-12 flex flex-col items-center justify-center text-center">
            <div className="w-24 h-24 rounded-full bg-gray-100 dark:bg-slate-700/50 flex items-center justify-center mb-6">
              <FontAwesomeIcon icon={faMoneyBillTransfer} className="text-4xl text-gray-400 dark:text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 dark:text-slate-200 mb-2">{t('payout.noPayout')}</h3>
            <p className="text-gray-500 dark:text-slate-400 text-sm max-w-xs">
              {t('payout.noPayoutDesc')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePayoutPage;
